/**
 * Settings Page - Combined Organization and Profile Settings
 * Tab-based layout showing different settings based on user permissions
 */

import { useState } from 'react';
import styled from 'styled-components';
import { Settings, User, Building2, CreditCard } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { OrganizationSettingsTab } from './OrganizationSettingsTab';
import { ProfileSettingsTab } from './ProfileSettingsTab';
import { BillingSettingsTab } from './BillingSettingsTab';

type TabType = 'organization' | 'profile' | 'billing';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${({ theme }) => theme.colors.primary.base};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.text.white};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Tab = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  border: none;
  background: transparent;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary.base : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  position: relative;
  transition: ${({ theme }) => theme.transitions.fast};

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme, $isActive }) =>
      $isActive ? theme.colors.primary.base : 'transparent'};
    transition: ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary.base};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TabContent = styled.div`
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

export const SettingsPage = () => {
  const { hasPermission, isSuperAdmin } = useAuthStore();

  // Check if user can view organization settings
  // Note: Super admins don't belong to any organization, so they manage orgs via Organization Management page
  const canViewOrgSettings = !isSuperAdmin() && hasPermission('org:settings:manage');
  // Billing tab: org admins can view their own subscription/invoices (not super admins)
  const canViewBilling = !isSuperAdmin() && hasPermission('org:settings:manage');

  // Default to organization tab if user has permission, otherwise profile
  const [activeTab, setActiveTab] = useState<TabType>(
    canViewOrgSettings ? 'organization' : 'profile',
  );

  return (
    <Container>
      <Header>
        <HeaderIcon>
          <Settings />
        </HeaderIcon>
        <HeaderText>
          <Title>Settings</Title>
          <Subtitle>Manage your account and organization preferences</Subtitle>
        </HeaderText>
      </Header>

      <TabContainer>
        {canViewOrgSettings && (
          <Tab
            $isActive={activeTab === 'organization'}
            onClick={() => setActiveTab('organization')}
          >
            <Building2 />
            Organization
          </Tab>
        )}
        {canViewBilling && (
          <Tab $isActive={activeTab === 'billing'} onClick={() => setActiveTab('billing')}>
            <CreditCard />
            Billing
          </Tab>
        )}
        <Tab $isActive={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
          <User />
          Profile
        </Tab>
      </TabContainer>

      <TabContent>
        {activeTab === 'organization' && canViewOrgSettings && <OrganizationSettingsTab />}
        {activeTab === 'billing' && canViewBilling && <BillingSettingsTab />}
        {activeTab === 'profile' && <ProfileSettingsTab />}
      </TabContent>
    </Container>
  );
};

export default SettingsPage;
